// AllocationEditor — POST or PATCH. BR-07 (sum exactly = milestone
// amount) is server-enforced; we surface the blocking modal on the
// allocation_sum_mismatch response.

import { useEffect, useMemo, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ModalHost';
import { useToast } from '@/components/ToastHost';
import { allocationsApi, peopleApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { mapApiErrorToFormErrors } from '@/utils/formErrors';
import { useDirtyFormReloadGuard } from '@/utils/useDirtyFormReloadGuard';
import { ApiError } from '@/types/errors';
import type { Allocation, Milestone, Person } from '@/types/domain';
import { formatCurrencyString } from '@/utils/format';

interface Props {
  open: boolean;
  onClose: () => void;
  milestone: Milestone;
  allocation: Allocation | null;
}

interface FormShape {
  person_id: string;
  amount: string;
}

const KNOWN: string[] = ['person_id', 'amount'];

export function AllocationEditor({ open, onClose, milestone, allocation }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = !!allocation;

  const peopleQ = useQuery({
    queryKey: qk.peopleAll(),
    queryFn: () => peopleApi.list({ limit: 100 }),
    staleTime: 60_000,
    enabled: open,
  });
  const peopleById = useMemo(() => {
    const m = new Map<string, Person>();
    for (const p of peopleQ.data?.data ?? []) m.set(p.id, p);
    return m;
  }, [peopleQ.data]);

  const defaults = useMemo<FormShape>(
    () =>
      allocation
        ? { person_id: allocation.person_id, amount: allocation.amount }
        : { person_id: '', amount: '' },
    [allocation],
  );

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting, dirtyFields, isDirty },
  } = useForm<FormShape>({ defaultValues: defaults });
  useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  // Browser reload-warning safety net per G-FE-1.
  useDirtyFormReloadGuard(open && isDirty);

  const [blockingMismatch, setBlockingMismatch] = useState<{
    milestoneAmount: string;
    sumAfter: string;
    diff: string;
  } | null>(null);

  const createM = useMutation({
    mutationFn: (body: FormShape) =>
      allocationsApi.create(milestone.id, {
        person_id: body.person_id,
        amount: body.amount,
      }),
  });
  const patchM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<{ amount: string }> }) =>
      allocationsApi.update(id, body),
  });

  const onSubmit: SubmitHandler<FormShape> = async (values) => {
    try {
      if (isEdit && allocation) {
        const body: { amount?: string } = {};
        if (dirtyFields.amount) body.amount = values.amount;
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        await patchM.mutateAsync({ id: allocation.id, body });
        toast.show({ severity: 'success', message: 'Allocation saved.' });
      } else {
        await createM.mutateAsync(values);
        toast.show({ severity: 'success', message: 'Allocation added.' });
      }
      void qc.invalidateQueries({
        queryKey: qk.milestoneAllocations(milestone.id),
      });
      onClose();
    } catch (err) {
      const mapped = mapApiErrorToFormErrors(err, KNOWN);
      for (const [field, message] of Object.entries(mapped.fieldErrors)) {
        setError(field as keyof FormShape, { type: 'server', message });
      }
      if (mapped.blockingModal === 'allocation_sum_mismatch' && err instanceof ApiError) {
        setBlockingMismatch({
          milestoneAmount: String(err.details['milestone_amount'] ?? milestone.amount),
          sumAfter: String(err.details['allocation_sum_after_save'] ?? '?'),
          diff: String(err.details['difference'] ?? '?'),
        });
      } else if (mapped.formMessage) {
        toast.show({ severity: 'error', message: mapped.formMessage });
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit allocation' : 'Add allocation'}
      role="dialog"
      canClose={() => !isDirty || window.confirm('Discard unsaved changes?')}
    >
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className="form-field">
          <label htmlFor="al-person">
            Person<span className="form-required">*</span>
          </label>
          {isEdit && allocation ? (
            <input
              value={
                peopleById.get(allocation.person_id)?.name ?? allocation.person_id
              }
              readOnly
              disabled
            />
          ) : (
            <select
              id="al-person"
              disabled={isSubmitting || peopleQ.isPending}
              {...register('person_id', { required: 'Choose a person.' })}
            >
              <option value="">Select a person…</option>
              {peopleQ.data?.data.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.email})
                </option>
              ))}
            </select>
          )}
          {errors.person_id ? (
            <span className="form-error">{errors.person_id.message}</span>
          ) : null}
        </div>
        <div className="form-field">
          <label htmlFor="al-amount">
            Amount (USD)<span className="form-required">*</span>
          </label>
          <input
            id="al-amount"
            inputMode="decimal"
            placeholder={milestone.amount}
            disabled={isSubmitting}
            {...register('amount', {
              required: 'Amount is required.',
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: 'Enter dollars and cents.',
              },
              validate: (v) => Number(v) > 0 || 'Amount must be greater than 0.',
            })}
          />
          {errors.amount ? (
            <span className="form-error">{errors.amount.message}</span>
          ) : null}
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isEdit ? 'Save' : 'Add allocation'}
          </button>
        </div>
      </form>

      {blockingMismatch ? (
        <Modal
          open={true}
          onClose={() => setBlockingMismatch(null)}
          title="Allocations don't sum to milestone amount"
          role="alertdialog"
          scrimDismissDisabled
        >
          <p>
            Allocations on this milestone would sum to{' '}
            <strong>{formatCurrencyString(blockingMismatch.sumAfter)}</strong>;
            the milestone amount is{' '}
            <strong>{formatCurrencyString(blockingMismatch.milestoneAmount)}</strong>.
            Adjust to match.
          </p>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setBlockingMismatch(null)}
            >
              Adjust
            </button>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}
